import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AppRoutes } from "./Routes";
import { useIdleTimer } from "react-idle-timer";
import { useSelector, useDispatch } from "react-redux";
import { useConfirmDialog } from "./hooks/useConfirmDialog";
import { logout } from "./components/api/AuthService";
import { userlogin, userlogout } from "./components/slice/authService";
import { axios } from "./lib/axios";
import { axiosSAML } from "./lib/axiosSAML";
import { apiUrl, samlApiUrl, COGNITO_CLIENT_ID, SAML_REDIRECT_URI } from "./environment";
import { isSAMLLogin, triggerBackgroundSamlLogout, POST_LOGIN_REDIRECT_KEY } from "./utils/authUtils";
import { getAccessToken, getRefreshToken } from "./utils/authStorage";
import {
  initAuthSessionSync,
  registerAuthTabId,
  requestSessionFromPeers,
} from "./utils/authSessionSync";
import { applySessionAuth, endSession, hasValidSession } from "./utils/sessionAuth";
import { failed, success } from "./components/common/Toastify";
import { FullPageSpinner } from "./components/common/Spinner/FullPageSpinner";
import ModalPopup from "./components/apps/CTI/ModalPopup";
import moment from "moment";
import { createEncounterInbound } from "./components/apps/api/AppointmentRequests";
import { useWebSocket, WebSocketProvider } from "./ContextsAndProvider/websocketContext";
import InBoundCall from "./components/Admin/Dashboard/InBoundCall";
import { editCTIData } from "./components/api/CTI";


function AppWrapper() {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state?.auth?.isLoggedIn);
  const userDetails = useSelector((state) => state?.auth?.user);
  const { getConfirmation, closeConfirmation } = useConfirmDialog();
  const intervalRef = useRef(null);
  const tokenRefreshMinutes = 10;
  const inactivityMinutes = 20;
  const promptMinutes = 2;

  const location = useLocation();
  const navigate = useNavigate();
  const { lastMessage } = useWebSocket();
  const [modalData, setModalData] = useState(null);
  const hasAttemptedSAMLLogin = useRef(false);
  const loginAPICallRef = useRef(null);
  const samlTokenRefreshRef = useRef(null);
  const [isLoading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(true);

  const idleChannel = useRef(null);
  const tabId = useRef(Date.now());
  const activeTabs = useRef(new Set());
  const [CTIModalShow, setCTIModalShow] = useState(false);
  const [inboundCallModalShow, setInboundCallModalShow] = useState(false);
  const [encounterData, setEncounterData] = useState(null);
  const [encounterLoading, setEncounterLoading] = useState(false);

  const [sessionBootstrapping, setSessionBootstrapping] = useState(false);

  const handleRefreshFailure = useCallback(() => {
    clearInterval(intervalRef.current);
    endSession(dispatch, {
      tabId: tabId.current,
      broadcast: true,
      clearPersistedAppStorage: true,
    });
  }, [dispatch]);

  const loginAPICall = useCallback(
    async (data) => {
      try {
        if (data) {
          const response = await axios.post(`${apiUrl}auth/admin-login`, data);
          if (response?.status === true) {
            const updatedUser = {
              ...userDetails,
              exp: response?.exp,
            };
            dispatch(userlogin(updatedUser));
            applySessionAuth(
              dispatch,
              {
                accessToken: response?.token,
                refreshToken: response?.refresh_token ?? getRefreshToken(),
              },
              { tabId: tabId.current }
            );
          } else {
            throw new Error("Login Failed: Response status not true.");
          }
        }
      } catch (error) {
        handleRefreshFailure();
        console.error("Token Refresh Error:", error);
      }
    },
    [dispatch, handleRefreshFailure, userDetails]
  );

  const samlTokenRefresh = useCallback(
    async (token) => {
      try {
        if (token) {
          const data = new URLSearchParams({
            grant_type: "refresh_token",
            client_id: COGNITO_CLIENT_ID,
            refresh_token: token,
          });
          const response = await axiosSAML.post(`${samlApiUrl}/oauth2/token`, data, {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          });
          if (response?.access_token && response?.id_token) {
            applySessionAuth(
              dispatch,
              {
                accessToken: response?.id_token,
                refreshToken: response?.refresh_token ?? token,
              },
              { tabId: tabId.current }
            );
          } else {
            failed(response?.message);
            throw new Error("Token Refresh Failed: No tokens returned.");
          }
        }
      } catch (error) {
        handleRefreshFailure();
        console.error("Token Refresh Error:", error);
      }
    },
    [dispatch, handleRefreshFailure]
  );

  const runTokenRefresh = useCallback(() => {
    const refresh = getRefreshToken();
    if (!refresh) {
      return;
    }
    if (isSAMLLogin()) {
      samlTokenRefresh(refresh);
    } else {
      loginAPICall({ refresh_token: refresh });
    }
  }, [loginAPICall, samlTokenRefresh]);

  loginAPICallRef.current = loginAPICall;
  samlTokenRefreshRef.current = samlTokenRefresh;

  useEffect(() => {
    registerAuthTabId(tabId.current);

    const cleanupSync = initAuthSessionSync(tabId.current, {
      onRemoteSession: ({ accessToken, refreshToken }) => {
        if (accessToken && refreshToken) {
          applySessionAuth(
            dispatch,
            { accessToken, refreshToken },
            { broadcast: false }
          );
        }
      },
      onRemoteCleared: () => {
        endSession(dispatch, { broadcast: false });
        closeConfirmation();
      },
    });

    return cleanupSync;
  }, [dispatch, closeConfirmation]);

  useEffect(() => {
    idleChannel.current = new BroadcastChannel("idle-sync");

    idleChannel.current.onmessage = (event) => {
      const { type, tab } = event.data;
      if (type === "ACTIVE") {
        activeTabs.current.add(tab);
      } else if (type === "IDLE") {
        activeTabs.current.delete(tab);
      } else if (type === "LOGOUT") {
        endSession(dispatch, { broadcast: false });
        closeConfirmation();
      }
    };

    return () => {
      idleChannel.current?.close();
    };
  }, [dispatch, closeConfirmation]);

  useEffect(() => {
    if (!isLoggedIn) {
      setSessionReady(true);
      setSessionBootstrapping(false);
      closeConfirmation();
      return;
    }

    let cancelled = false;
    setSessionBootstrapping(true);
    setSessionReady(false);

    const BOOTSTRAP_TIMEOUT_MS = 15000;

    const finishBootstrap = (ready) => {
      if (cancelled) {
        return;
      }
      clearTimeout(bootstrapTimeout);  // <-- add this
      setSessionBootstrapping(false);
      setSessionReady(ready);
    };

    const bootstrapTimeout = setTimeout(() => {
      if (cancelled) return;
      console.warn("Session bootstrap timed out");
      endSession(dispatch, { tabId: tabId.current, clearPersistedAppStorage: true });
      navigate("/login");
      finishBootstrap(true);
    }, BOOTSTRAP_TIMEOUT_MS);

    const bootstrapSession = async () => {
      try {
      if (hasValidSession()) {
        applySessionAuth(
          dispatch,
          {
            accessToken: getAccessToken(),
            refreshToken: getRefreshToken(),
          },
          { broadcast: false }
        );
        finishBootstrap(true);
        return;
      }

      const peerSession = await requestSessionFromPeers(tabId.current);
      if (cancelled) {
        return;
      }
      if (peerSession?.accessToken && peerSession?.refreshToken) {
        applySessionAuth(dispatch, peerSession, { broadcast: false });
        finishBootstrap(true);
        return;
      }

      const refresh = getRefreshToken();
      if (refresh) {
        if (isSAMLLogin()) {
          await samlTokenRefreshRef.current(refresh);
        } else {
          await loginAPICallRef.current({ refresh_token: refresh });
        }
        if (cancelled) {
          return;
        }
        if (hasValidSession()) {
          finishBootstrap(true);
        } else {
          endSession(dispatch, { tabId: tabId.current, clearPersistedAppStorage: true });
          navigate("/login");
          finishBootstrap(true);
        }
        return;
      }

      endSession(dispatch, { tabId: tabId.current, clearPersistedAppStorage: true });
      navigate("/login");
      finishBootstrap(true);
      }
      catch (e) {
        console.error("Session bootstrap failed:", e);
        endSession(dispatch, { tabId: tabId.current, clearPersistedAppStorage: true });
        navigate("/login");
      } finally {
        finishBootstrap(true);
      }
    };

    bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, dispatch, navigate, closeConfirmation]);

  const markActive = () => {
    idleChannel.current?.postMessage({ type: "ACTIVE", tab: tabId.current });
  };

  const markIdle = () => {
    idleChannel.current?.postMessage({ type: "IDLE", tab: tabId.current });
  };

  useEffect(() => {
    const handleActivity = () => {
      markActive();
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const samlCode = params.get("code");
    const currentPath = location.pathname;

    if (samlCode && !isLoggedIn && currentPath.includes("/login") && !hasAttemptedSAMLLogin.current) {
      hasAttemptedSAMLLogin.current = true;
      loginSAMLAttempt({
        code: samlCode,
        client_id: COGNITO_CLIENT_ID,
        grant_type: "authorization_code",
        redirect_uri: SAML_REDIRECT_URI,
      });
    }
  }, []);

  const samlLoginAPICall = async (data) => {
    try {
      setLoading(true);
      if (data) {
        const response = await axios.post(`${apiUrl}auth/admin-login-saml`, data);
        if (response?.authType === "SUCCESS") {
          setLoading(false);
          dispatch(userlogin(response.user));
          localStorage.setItem("saml_login", "true");
          applySessionAuth(
            dispatch,
            {
              accessToken: response?.token,
              refreshToken: response?.refresh_token,
            },
            { tabId: tabId.current }
          );
          success("Successfully Logged In");
          // Post-login redirect uses sessionStorage + LoginRouteElement (see Routes/index.js);
          // navigate() here loses the race to <Navigate to={dashboard} /> on /login.
        } else if (response?.result?.authType === "CREATE_ACCOUNT") {
          setLoading(false);
          sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
          navigate("/saml/account-setup", { state: { data: response?.result } });
        } else {
          failed(response?.message);
          throw new Error("Login Failed: Response status not true.");
        }
        setLoading(false);
      }
    } catch (error) {
      clearInterval(intervalRef.current);
      handleRefreshFailure();
      console.error("Token Refresh Error:", error);
      failed(error?.response?.data?.message || "Request failed");
      setLoading(false);
    }
  };

  const loginSAMLAttempt = async (data) => {
    try {
      setLoading(true);
      const response = await axios.post(`${samlApiUrl}/oauth2/token`, data, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      setLoading(false);
      if (response?.access_token) {
        samlLoginAPICall({
          access_token: response?.access_token,
          refresh_token: response?.refresh_token,
          id_token: response?.id_token,
        });
      }
    } catch (error) {
      console.error("SAML Login Error:", error);
    }
  };

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (isLoggedIn && sessionReady && getRefreshToken()) {
      intervalRef.current = setInterval(
        runTokenRefresh,
        1000 * 60 * tokenRefreshMinutes
      );
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isLoggedIn, sessionReady, runTokenRefresh]);

  const handleOnIdle = () => {
    markIdle();

    setTimeout(() => {
      if (activeTabs.current.size === 0 && isLoggedIn) {
        idleChannel.current?.postMessage({ type: "LOGOUT" });
        clearInterval(intervalRef.current);
        closeConfirmation();
        const wasSaml = logout();
        dispatch(userlogout());

        if (wasSaml) {
          triggerBackgroundSamlLogout();
        }
      }
    }, 500);
  };

  const handlePrompt = async () => {
    if (activeTabs.current.size > 1) {
      return;
    }

    if (isLoggedIn) {
      const confirmed = await getConfirmation({
        title: "Attention!",
        message: "You will be logged out unless you press continue.",
        actionBtnTitle: "Continue",
        showCancel: false,
      });

      if (confirmed) {
        activate();
        markActive();
      }
    } else {
      activate();
      markActive();
    }
  };

  const { activate } = useIdleTimer({
    timeout: 1000 * 60 * inactivityMinutes,
    promptBeforeIdle: 1000 * 60 * promptMinutes,
    onIdle: handleOnIdle,
    onPrompt: handlePrompt,
  });

  useEffect(() => {
    if (lastMessage && lastMessage?.callType && lastMessage?.callType?.toLowerCase() === "inbound") {
      setModalData(lastMessage);
      setCTIModalShow(true);
    }
  }, [lastMessage]);

  const handleProceedClick = (data) => {
    setCTIModalShow(false);
    setEncounterLoading(true);
    let encounterPayload = {
      orgId: data?.orgId,
      orgName: data?.orgName,
      serviceProvider: {
        type: "Organization",
        display: data?.orgName,
        reference: `Organization/${data?.orgId}`,
      },
      requestedPeriod: {
        start: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
        end: null,
      },
      practitionerId: data?.practitionerId,
      practitionerName: data?.practitionerName,
      status: "in-progress-intake",
      statusHistory: [
        {
          status: "in-progress-intake",
          actor: {
            reference: `Practitioner/${data.practitionerId}`,
            display: data.practitionerName,
          },
          period: {
            start: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
            end: null,
          },
        },
      ],
    };

    if (data?.serviceLineId) {
      encounterPayload.serviceLineId = data?.serviceLineId;
      encounterPayload.serviceLine = {
        display: data?.serviceLineName,
        reference: `ServiceLine/${data?.serviceLineId}`,
        type: "ServiceLine",
      };
    }

    createEncounterInbound(encounterPayload)
      .then((res) => {
        if (res.status === true) {
          let encounterResData = res?.data;
          editCTIData(data?.contactId, data?.orgId, {
            encounterId: res?.data?.id,
            callType: "INBOUND",
          }).then((res) => {
            if (res?.status === true) {
              setEncounterData(encounterResData);
              setInboundCallModalShow(true);
            } else {
              setEncounterData(res.data);
              setInboundCallModalShow(true);
            }
          });
        } else {
          failed(
            res?.response?.data?.message ||
              res?.response?.data?.error ||
              res.message
          );
          setInboundCallModalShow(false);
        }
      })
      .catch((res) => {
        setInboundCallModalShow(false);
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      })
      .finally(() => setEncounterLoading(false));
  };

  const handleCancelClick = () => {
    setModalData(null);
    setCTIModalShow(false);
  };

  const showSessionSpinner =
    isLoggedIn && (sessionBootstrapping || !sessionReady);

  return (
    <div className="App">
      {isLoading && <FullPageSpinner loadingText="Signing in..." />}
      {showSessionSpinner && (
        <FullPageSpinner loadingText="Restoring session..." />
      )}
      {encounterLoading && <FullPageSpinner loadingText="Loading Encounter..." />}
      {CTIModalShow && (
        <ModalPopup
          data={modalData}
          onShow={CTIModalShow}
          onHide={() => handleCancelClick()}
          handleProceedClick={handleProceedClick}
        />
      )}
      {inboundCallModalShow && (
        <InBoundCall
          encounterId={encounterData?.id}
          encounteredData={encounterData}
          modalShow={inboundCallModalShow}
          handleShow={() => setInboundCallModalShow(false)}
        />
      )}
      {!showSessionSpinner && <AppRoutes />}
      <ToastContainer />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <WebSocketProvider>
        <AppWrapper />
      </WebSocketProvider>
    </Router>
  );
}
